// src/pages/MyBookings.jsx — FINAL FULL (WITH DISTANCE PREF)
// ✅ Start/End show full date+time
// ✅ Commute Preference (editable by user) via /api/my/commute (GET/PUT/DELETE) with safe fallback
// ✅ NEW: Estimated Distance Preference (editable) via /api/my/commute (same endpoint; backward compatible)
// ✅ Shows Distance in booking cards (fallback to saved preference)
// ✅ Does NOT break existing bookings behaviors (edit/delete/list/status/dropdown)
// ✅ Fix dropdown click-outside glitch (uses pointerdown + stopPropagation)

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../lib/api.js';
import toast from 'react-hot-toast';
import Card from '../components/Card.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import Modal from '../components/Modal.jsx';
import ConfirmDialog from '../components/ConfirmDialog.jsx';
import {
  Car,
  Monitor,
  Clock,
  MapPin,
  Pencil as Edit3,
  Trash2,
  MoreVertical,
  Bike,
  Route,
  Unlock
} from 'lucide-react';

/* ===================== Helpers waktu (LOKAL) ===================== */
const fromInputLocal = (str) => {
  if (!str) return null;
  const [day, time] = str.split('T');
  const [Y, M, D] = day.split('-').map(Number);
  const [h, m] = time.split(':').map(Number);
  return new Date(Y, M - 1, D, h, m, 0, 0);
};
const pad = (n) => String(n).padStart(2, '0');
const toSqlLocal = (d) => {
  if (!(d instanceof Date)) d = new Date(d);
  const Y = d.getFullYear();
  const M = pad(d.getMonth() + 1);
  const D = pad(d.getDate());
  const h = pad(d.getHours());
  const m = pad(d.getMinutes());
  const s = pad(d.getSeconds());
  return `${Y}-${M}-${D} ${h}:${m}:${s}`;
};
const localSqlToInput = (sql) => (sql ? sql.replace(' ', 'T').slice(0, 16) : '');
const parseLocalSqlToDate = (sql) => (sql ? new Date(sql.replace(' ', 'T')) : null);

const MONTH = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const fmtDateTime = (sql) => {
  if (!sql) return '';
  const d = parseLocalSqlToDate(sql);
  if (!d || isNaN(d)) return '';
  return `${MONTH[d.getMonth()]} ${String(d.getDate()).padStart(2,'0')}, ${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
/* ================================================================ */

const COMMUTE_OPTIONS = [
  'Walk',
  'Bicycle',
  'E-Bike',
  'Motorcycle',
  'Motorcycle with a colleague',
  'Car',
  'Car with a colleague',
  'Public transport',
];

const DISTANCE_OPTIONS = [
  '0-5 km',
  '5-10 km',
  '10-20 km',
  '20 - 30 km',
  '30+ km',
];

function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editModal, setEditModal] = useState({ open: false, booking: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, booking: null });
  const [releaseDialog, setReleaseDialog] = useState({ open: false, booking: null });

  const [submitting, setSubmitting] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // tick untuk refresh status realtime
  const [tick, setTick] = useState(0);

  const [editForm, setEditForm] = useState({
    start_time: '',
    end_time: '',
    notes: ''
  });

  // Commute preference (user-editable)
  const [commuteLoading, setCommuteLoading] = useState(true);
  const [commuteSaving, setCommuteSaving] = useState(false);
  const [savedCommute, setSavedCommute] = useState('');
  const [draftCommute, setDraftCommute] = useState('');

  // ✅ Distance preference (user-editable)
  const [savedDistance, setSavedDistance] = useState('');
  const [draftDistance, setDraftDistance] = useState('');

  const dropdownRef = useRef(null);

  const isParkingType = useMemo(() => (t) => String(t || '').toLowerCase() === 'parking', []);

  useEffect(() => {
    fetchBookings();
    fetchCommuteAndDistance();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const tm = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(tm);
  }, []);

  const fetchBookings = useCallback(async () => {
    try {
      const { data } = await api.get('/bookings', { params: { user_only: true } });
      const visible = (Array.isArray(data) ? data : []).filter(b => b.status !== 'cancelled');
      setBookings(visible);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCommuteAndDistance = useCallback(async () => {
    setCommuteLoading(true);
    try {
      const { data } = await api.get('/my/commute');
      const v = (data?.commute || '').trim();
      const d = (data?.estimated_distance || data?.estimatedDistance || '').trim();

      setSavedCommute(v);
      setDraftCommute(v);

      setSavedDistance(d);
      setDraftDistance(d);
    } catch (e) {
      console.error('Failed to fetch commute/distance:', e);
      setSavedCommute('');
      setDraftCommute('');
      setSavedDistance('');
      setDraftDistance('');
    } finally {
      setCommuteLoading(false);
    }
  }, []);

  const savePrefs = useCallback(async () => {
    const commuteVal = (draftCommute || '').trim();
    const distVal = (draftDistance || '').trim();

    setCommuteSaving(true);
    try {
      await api.put('/my/commute', {
        commute: commuteVal || null,
        estimated_distance: distVal || null,
      });

      setSavedCommute(commuteVal);
      setSavedDistance(distVal);

      toast.success('Preferences saved.');
      fetchBookings(); // refresh supaya card ikut update (fallback)
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save preferences');
    } finally {
      setCommuteSaving(false);
    }
  }, [draftCommute, draftDistance, fetchBookings]);

  const clearPrefs = useCallback(async () => {
    setCommuteSaving(true);
    try {
      await api.delete('/my/commute');
      setSavedCommute('');
      setDraftCommute('');
      setSavedDistance('');
      setDraftDistance('');
      toast.success('Preferences cleared.');
      fetchBookings();
    } catch (e) {
      // fallback: clear via PUT
      try {
        await api.put('/my/commute', { commute: null, estimated_distance: null });
        setSavedCommute('');
        setDraftCommute('');
        setSavedDistance('');
        setDraftDistance('');
        toast.success('Preferences cleared.');
        fetchBookings();
      } catch (e2) {
        toast.error(e2.response?.data?.error || 'Failed to clear preferences');
      }
    } finally {
      setCommuteSaving(false);
    }
  }, [fetchBookings]);

  const handleEdit = useCallback((booking) => {
    setEditForm({
      start_time: localSqlToInput(booking.start_local || booking.start_time),
      end_time: localSqlToInput(booking.end_local || booking.end_time),
      notes: booking.notes || ''
    });
    setEditModal({ open: true, booking });
    setActiveDropdown(null);
  }, []);

  const handleDelete = useCallback((booking) => {
    setDeleteDialog({ open: true, booking });
    setActiveDropdown(null);
  }, []);

  const submitEdit = useCallback(async (ev) => {
    ev.preventDefault();
    if (!editModal.booking) return;

    const s = fromInputLocal(editForm.start_time);
    const eTime = fromInputLocal(editForm.end_time);
    if (!s || !eTime) return toast.error('Invalid date/time');
    if (eTime <= s)   return toast.error('End time must be after start time');

    setSubmitting(true);
    try {
      await api.put(`/bookings/${editModal.booking.id}`, {
        start_time: toSqlLocal(s),
        end_time: toSqlLocal(eTime),
        notes: editForm.notes || ''
      });

      toast.success('Booking updated successfully!');
      setEditModal({ open: false, booking: null });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update booking');
    } finally {
      setSubmitting(false);
    }
  }, [editModal.booking, editForm.start_time, editForm.end_time, editForm.notes, fetchBookings]);

  const confirmDelete = useCallback(async () => {
    if (!deleteDialog.booking) return;
    const id = deleteDialog.booking.id;

    setBookings(prev => prev.filter(b => b.id !== id));
    setSubmitting(true);
    try {
      await api.delete(`/bookings/${id}`);
      toast.success('Booking removed from your list.');
      setDeleteDialog({ open: false, booking: null });
    } catch (error) {
      await fetchBookings();
      toast.error(error.response?.data?.error || 'Failed to delete booking');
    } finally {
      setSubmitting(false);
    }
  }, [deleteDialog.booking, fetchBookings]);

  const handleRelease = useCallback((booking) => {
    setReleaseDialog({ open: true, booking });
    setActiveDropdown(null);
  }, []);

  const confirmRelease = useCallback(async () => {
    if (!releaseDialog.booking) return;
    const id = releaseDialog.booking.id;

    setSubmitting(true);
    try {
      await api.put(`/bookings/${id}/release`);
      const nowSql = new Date().toISOString().slice(0, 19).replace('T', ' ');
      setBookings(prev => prev.map(b => (b.id === id ? { ...b, status: 'released', end_time: nowSql } : b)));
      toast.success('Booking released.');
      setReleaseDialog({ open: false, booking: null });
      await fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to release booking');
    } finally {
      setSubmitting(false);
    }
  }, [releaseDialog.booking, fetchBookings]);

  /* ---------- Status (Active/Complete) ---------- */
  const getVisualStatus = useCallback((booking) => {
    const raw = String(booking.status || '').toLowerCase();
    if (raw === 'cancelled') return 'cancelled';
    if (raw === 'released') return 'released';
    const endLocal = booking.end_local || booking.end_time;
    const ended = parseLocalSqlToDate(endLocal) <= new Date();
    return ended ? 'complete' : 'active';
  }, []);

  const getStatusLabel = useCallback((booking) => {
    const s = getVisualStatus(booking);
    if (s === 'complete') return 'Complete';
    if (s === 'cancelled') return 'Cancelled';
    if (s === 'released') return 'Released';
    return 'Active';
  }, [getVisualStatus]);

  const getStatusColor = useCallback((booking) => {
    const s = getVisualStatus(booking);
    if (s === 'active') return 'bg-green-100 text-green-800';
    if (s === 'complete') return 'bg-blue-100 text-blue-800';
    if (s === 'cancelled') return 'bg-red-100 text-red-800';
    if (s === 'released') return 'bg-amber-100 text-amber-800';
    return 'bg-gray-100 text-gray-800';
  }, [getVisualStatus]);

  const BookingCard = ({ booking, index }) => {
    void tick;

    const isParking = isParkingType(booking.type_name);
    const startSql = booking.start_local || booking.start_time;
    const endSql   = booking.end_local   || booking.end_time;

    const stillValid = parseLocalSqlToDate(endSql) > new Date();
    const status = getVisualStatus(booking);
    const canEdit = status === 'active' && stillValid;
    const canRelease = status === 'active' && isParking;

    const commuteText = (booking.commute || savedCommute || '').trim();
    const distanceText =
      (booking.estimated_distance || booking.estimatedDistance || savedDistance || '').trim();

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.06 }}
        className="relative"
      >
        <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-600">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              {isParking ? (
                <div className="p-2 bg-amber-600 rounded-lg">
                  <Car className="h-5 w-5 text-white" />
                </div>
              ) : (
                <div className="p-2 bg-emerald-600 rounded-lg">
                  <Monitor className="h-5 w-5 text-white" />
                </div>
              )}
              <div>
                <h3 className="font-semibold text-white text-lg">{booking.resource_name}</h3>
                <p className="text-gray-400 text-sm">
                  {booking.type_name} {booking.floor_name ? `• ${booking.floor_name}` : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(booking)}`}>
                {getStatusLabel(booking)}
              </span>

              <div
                ref={dropdownRef}
                className="relative"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveDropdown(activeDropdown === booking.id ? null : booking.id);
                  }}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>

                <AnimatePresence>
                  {activeDropdown === booking.id && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 top-full mt-1 w-44 bg-gray-800 border border-gray-700 rounded-lg shadow-lg z-10"
                    >
                      {canEdit && (
                        <button
                          onClick={() => handleEdit(booking)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2"
                        >
                          <Edit3 className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                      )}
                      {canRelease && (
                        <button
                          onClick={() => handleRelease(booking)}
                          className="w-full text-left px-4 py-2 text-sm text-amber-300 hover:bg-amber-600/10 hover:text-amber-200 flex items-center space-x-2"
                        >
                          <Unlock className="h-4 w-4" />
                          <span>Release</span>
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(booking)}
                        className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-600/10 hover:text-red-300 flex items-center space-x-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Delete</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center text-gray-300">
              <Clock className="h-4 w-4 mr-3 text-blue-400" />
              <div className="text-sm sm:text-base">
                <div className="flex flex-col sm:flex-row sm:items-center sm:gap-10">
                  <span><span className="text-gray-400">Start:</span> {fmtDateTime(startSql)}</span>
                  <span><span className="text-gray-400">End:</span> {fmtDateTime(endSql)}</span>
                </div>
              </div>
            </div>

            {booking.floor_name && (
              <div className="flex items-center text-gray-300">
                <MapPin className="h-4 w-4 mr-3 text-purple-400" />
                <span>{booking.floor_name}</span>
              </div>
            )}

            {commuteText && (
              <div className="flex items-center text-gray-300">
                <Bike className="h-4 w-4 mr-3 text-sky-400" />
                <span className="text-sm">
                  <span className="text-gray-400">Commute:</span> {commuteText}
                </span>
              </div>
            )}

            {distanceText && (
              <div className="flex items-center text-gray-300">
                <Route className="h-4 w-4 mr-3 text-amber-400" />
                <span className="text-sm">
                  <span className="text-gray-400">Estimated Distance:</span> {distanceText}
                </span>
              </div>
            )}

            {booking.notes && (
              <div className="bg-gray-700/50 rounded-lg p-3">
                <p className="text-gray-300 text-sm">
                  <span className="font-medium">Notes:</span> {booking.notes}
                </p>
              </div>
            )}
          </div>
        </Card>
      </motion.div>
    );
  };

  useEffect(() => {
    const handleOutside = () => setActiveDropdown(null);
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  const prefsChanged = draftCommute !== savedCommute || draftDistance !== savedDistance;

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 shadow-xl"
      >
        <h1 className="text-2xl font-bold text-white mb-2">My Bookings</h1>
        <p className="text-purple-100">Manage your parking and desk reservations</p>
      </motion.div>

      {/* Preferences Panel */}
      <Card className="bg-gray-800 border-gray-700">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-sky-600 rounded-lg">
              <Bike className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-white font-semibold">Commute & Distance Preferences</div>
              <div className="text-gray-400 text-sm">
                Use to do edit cummute habbit.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 w-full md:w-auto flex-wrap">
            {/* Commute */}
            <div className="w-full md:w-[280px]">
              <div className="text-xs text-gray-400 mb-1">Commute</div>
              <select
                value={draftCommute}
                onChange={(e) => setDraftCommute(e.target.value)}
                disabled={commuteLoading || commuteSaving}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">— Select —</option>
                {COMMUTE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              {!commuteLoading && (
                <div className="text-xs text-gray-400 mt-2">
                  Saved:{' '}
                  <span className="text-gray-200 font-medium">
                    {savedCommute || 'Not set'}
                  </span>
                </div>
              )}
            </div>

            {/* Distance */}
            <div className="w-full md:w-[220px]">
              <div className="text-xs text-gray-400 mb-1">Estimated Distance</div>
              <select
                value={draftDistance}
                onChange={(e) => setDraftDistance(e.target.value)}
                disabled={commuteLoading || commuteSaving}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">— Select —</option>
                {DISTANCE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>

              {!commuteLoading && (
                <div className="text-xs text-gray-400 mt-2">
                  Saved:{' '}
                  <span className="text-gray-200 font-medium">
                    {savedDistance || 'Not set'}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pt-5 md:pt-0">
              <Button
                onClick={savePrefs}
                disabled={commuteLoading || commuteSaving || !prefsChanged}
                className="whitespace-nowrap"
              >
                {commuteSaving ? 'Saving...' : 'Save'}
              </Button>

              <Button
                variant="secondary"
                onClick={clearPrefs}
                disabled={commuteLoading || commuteSaving || (!savedCommute && !savedDistance)}
                className="whitespace-nowrap"
              >
                Clear
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {bookings.length > 0 ? (
        <div className="space-y-4">
          {bookings.map((booking, index) => (
            <BookingCard key={booking.id} booking={booking} index={index} />
          ))}
        </div>
      ) : (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-12">
          <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
            <Monitor className="h-12 w-12 text-gray-500" />
          </div>
          <h3 className="text-xl font-medium text-white mb-2">No bookings yet</h3>
          <p className="text-gray-400 mb-6">
            You haven't made any bookings yet. Start by booking a parking slot or desk space.
          </p>
          <Button onClick={() => (window.location.href = '/booking')}>Make a Booking</Button>
        </motion.div>
      )}

      {/* Edit modal */}
      <Modal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, booking: null })}
        title="Edit Booking"
        size="md"
      >
        {editModal.booking && (
          <form onSubmit={submitEdit} className="space-y-4">
            <div className="bg-gray-700 rounded-lg p-4 mb-4">
              <div className="flex items-center space-x-2 mb-2">
                {editModal.booking.type_name === 'Parking'
                  ? <Car className="h-5 w-5 text-amber-400" />
                  : <Monitor className="h-5 w-5 text-emerald-400" />
                }
                <span className="font-semibold text-white">{editModal.booking.resource_name}</span>
              </div>
              {editModal.booking.floor_name && (
                <p className="text-gray-400 text-sm">{editModal.booking.floor_name}</p>
              )}
            </div>

            <Input
              label="Start Time"
              type="datetime-local"
              value={editForm.start_time}
              onChange={(e) => setEditForm(prev => ({ ...prev, start_time: e.target.value }))}
              required
            />

            <Input
              label="End Time"
              type="datetime-local"
              value={editForm.end_time}
              onChange={(e) => setEditForm(prev => ({ ...prev, end_time: e.target.value }))}
              required
            />

            <Input
              label="Notes (Optional)"
              value={editForm.notes}
              onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Add any additional notes..."
            />

            <div className="flex space-x-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEditModal({ open: false, booking: null })}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button type="submit" loading={submitting} className="flex-1">
                Update Booking
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={releaseDialog.open}
        onClose={() => setReleaseDialog({ open: false, booking: null })}
        onConfirm={confirmRelease}
        title="Release Booking"
        message={`Release your booking for "${releaseDialog.booking?.resource_name}"? This will free the slot for others today.`}
        type="danger"
        confirmText="Release"
        loading={submitting}
      />

      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, booking: null })}
        onConfirm={confirmDelete}
        title="Delete Booking"
        message={`Delete your booking for "${deleteDialog.booking?.resource_name}" from your list? (This won't remove the entry from admin reports.)`}
        type="danger"
        confirmText="Delete"
        loading={submitting}
      />
    </div>
  );
}

export default MyBookings;

